import { Injectable, Inject } from '@angular/core';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { getObjectTypeNameTextId } from '@fi-shared/utils/object-type-name';
import { DocumentModel } from '../../document/document.model';
import { TemplateModel } from '../../template/template.model';
import { MediaBankFileModel } from '../../media-bank/media-bank-file.model';
import { ForumModel } from '../../forum/forum.model';
import { UserModel } from '../../user/user.model';
import { RoleModel } from '../../role/role.model';
import { AreaModel } from '../../area/area.model';
import { BusinessAreaModel } from '../../business-area/business-area.model';
import { BusinessUnitModel } from '../../business-unit/business-unit.model';
import { ClusterModel } from '../../cluster/cluster.model';
import { TagModel } from '@fi-shared/models';
import { AgreementModel } from '@fi-shared/fi-agreement/agreement.model';
import { DataFieldGroupModel } from '../../data-field/data-field-group-model';
import { SmsModel } from '../../sms/sms.model';
import { ChecklistModel } from '../../checklist/checklist.model';
import { AppTextService } from '@fi-shared/app-text';
import { getObjectViewStateData } from '@fi-app/shared/utils/get-object-view-state-data';
import { RawParams } from '@uirouter/angular';

export interface IObjectIdName {
    id: number;
    name: string;
    objectType: number;
}

@Injectable()
export class ObjectTypesService {
    constructor(private appTextService: AppTextService) {}

    GetNameByObjectType(objectType: ObjectTypes, isPlural?: boolean): string {
        return this.appTextService.get(getObjectTypeNameTextId(objectType, isPlural));
    }

    getObjectViewStateName(objectType: ObjectTypes) {
        const stateData = getObjectViewStateData(objectType, undefined);
        if (stateData) {
            return stateData.sref;
        }
        throw 'FI: ObjectTypeService.getObjectViewStateName: unexpected object type "' + objectType + '"';
    }

    getTextIdForTitleCreate(objectType: ObjectTypes): string {
        switch (objectType) {
            case ObjectTypes.User:
                return 'common.new_user';
            case ObjectTypes.Role:
                return 'role.new_role';
            case ObjectTypes.BusinessUnit:
                return 'businessunit.new_unit';
            case ObjectTypes.Area:
                return 'area.new_area';
            case ObjectTypes.BusinessArea:
                return 'businessarea.new_businessarea';
            case ObjectTypes.Cluster:
                return 'cluster.new_cluster';
            case ObjectTypes.Document:
                return 'common.new_page';
            case ObjectTypes.Tag:
                return 'tag.new_tag';
            case ObjectTypes.ExtendedRoleRight:
                return 'common.new_extended_right';
            case ObjectTypes.MediaBank:
                return 'common.new_file';
            case ObjectTypes.Template:
            case ObjectTypes.CheckListTemplate:
                return 'common.new_template';
            case ObjectTypes.Invoice:
                return 'billing.new_invoice';
            case ObjectTypes.BillingPriceArticle:
                return 'billing.new_price_article';
            case ObjectTypes.SiteMap:
                return 'sitemap.new_sitemap';
            case ObjectTypes.CustomerBillingCustomer:
                return 'billing.new_customer';
            case ObjectTypes.Currency:
                return 'billing.new_currency_rate';
            case ObjectTypes.CustomerBillingReseller:
                return 'billing.new_reseller';
            case ObjectTypes.Forum:
                return 'forum.new_forum';
            case ObjectTypes.SMSMessage:
                return 'new_sms';
            case ObjectTypes.Form:
                return 'form.new_form';
            case ObjectTypes.Task:
                return 'task.new_task';
            default:
                return null;
        }
    }

    getObjectIdName(item): IObjectIdName {
        // Pages
        if (item instanceof DocumentModel) return result(item.Id, item.Name, ObjectTypes.Document);
        if (item instanceof TemplateModel) return result(item.Id, item.Name, ObjectTypes.Template);

        // Files
        if (item instanceof MediaBankFileModel) return result(item.Id, item.Name, ObjectTypes.MediaBank);

        // Forum
        if (item instanceof ForumModel) return result(item.Id, item.Name, ObjectTypes.Forum);

        // Our organisation
        if (item instanceof UserModel) return result(item.Id, item.FullName, ObjectTypes.User);
        if (item instanceof RoleModel) return result(item.Id, item.Name, ObjectTypes.Role);
        if (item instanceof AreaModel) return result(item.Id, item.Name, ObjectTypes.Area);
        if (item instanceof BusinessAreaModel) return result(item.Id, item.Name, ObjectTypes.BusinessArea);
        if (item instanceof BusinessUnitModel) return result(item.Id, item.Name, ObjectTypes.BusinessUnit);
        if (item instanceof ClusterModel) return result(item.Id, item.Name, ObjectTypes.Cluster);

        // Global settings
        if (item instanceof TagModel) return result(item.Id, item.Name, ObjectTypes.Tag);
        if (item instanceof AgreementModel) return result(item.Id, item.Name, ObjectTypes.Agreement);
        if (item instanceof DataFieldGroupModel) return result(item.Id, '', ObjectTypes.DataFieldGroup);

        // Checklists
        if (item instanceof ChecklistModel) {
            var objectType = !item.IsTemplate ? ObjectTypes.CheckList : ObjectTypes.CheckListTemplate;
            return result(item.Id, item.Name, objectType);
        }
        // SMS
        if (item instanceof SmsModel) return result(item.Id, undefined, ObjectTypes.SMSMessage);

        function result(id: number, name: string, objectType: number) {
            return <IObjectIdName>{
                id: id,
                name: name,
                objectType: objectType,
            };
        }
    }
}
